import type { ProgresoEjercicio } from './ejercicio';

/**
 * Contrato de `GET /gamificacion/estadisticas`.
 * Fuente: backend/src/models/repositories/UsuarioRepository.ts -> getStatistics()
 */
export interface EstadisticasUsuario {
  ejerciciosCompletados: number;
  evaluacionesCompletadas: number;
  postsComunidad: number;
  puntos: number;
  logrosDesbloqueados: number;
  nivel: string;
  /** Dias consecutivos con ejercicio completado. Un dia fallido lo reinicia
   *  (UsuarioRepository.getStatistics), por eso la UI no lo usa como castigo. */
  rachaDias: number;
}

/* ------------------------------------------------------- maquina de estados -- */

/**
 * Estados de la pantalla del dashboard. Antes solo existia `cargando`: el resto
 * colapsaba en "toast + ceros", lo que hacia que "no tengo datos" y "fallo la
 * API" se vieran identicos.
 */
export type EstadoDashboard =
  | 'cargando'
  | 'primer-uso'
  | 'con-datos'
  | 'parcial'
  | 'sin-red'
  | 'error'
  | 'redirigiendo';

export function clasificarDashboard(
  stats: EstadisticasUsuario | null,
  online: boolean,
  error: unknown,
): EstadoDashboard {
  if (error) return online ? 'error' : 'sin-red';
  if (!stats) return 'cargando';
  const total =
    stats.ejerciciosCompletados +
    stats.evaluacionesCompletadas +
    stats.postsComunidad;
  if (total === 0) return 'primer-uso';
  if (stats.ejerciciosCompletados === 0) return 'parcial';
  return 'con-datos';
}

/* ------------------------------------------------------------------ nivel -- */

/**
 * Escalera de niveles. Replica `UsuarioRepository.calculateLevel` para que el
 * progreso de la UI no invente umbrales distintos a los del backend.
 */
export const NIVELES = [
  { nombre: 'Explorador Mental', min: 0 },
  { nombre: 'Guardián', min: 500 },
  { nombre: 'Maestro', min: 1500 },
  { nombre: 'Líder', min: 3000 },
] as const;

export function progresoNivel(puntos: number): {
  pct: number;
  faltan: number;
  siguiente: string | null;
} {
  let i = 0;
  for (let k = NIVELES.length - 1; k >= 0; k -= 1) {
    if (puntos >= NIVELES[k].min) {
      i = k;
      break;
    }
  }

  const actual = NIVELES[i];
  const siguiente = NIVELES[i + 1];
  if (!siguiente) return { pct: 100, faltan: 0, siguiente: null };

  const rango = siguiente.min - actual.min;
  const avance = puntos - actual.min;
  return {
    pct: Math.max(0, Math.min(100, Math.round((avance / rango) * 100))),
    faltan: siguiente.min - puntos,
    siguiente: siguiente.nombre,
  };
}

/* ------------------------------------------------------- dias de la semana -- */

/** Etiquetas indexadas por `Date.getDay()` (0 = domingo). */
const ETIQUETAS_DIA = ['D', 'L', 'M', 'X', 'J', 'V', 'S'] as const;

export interface DiaSemana {
  /** `YYYY-MM-DD` en hora local. */
  iso: string;
  /** Inicial del dia para la celda. */
  dia: string;
  fecha: Date;
  esHoy: boolean;
  completado: boolean;
}

const aIsoLocal = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Los ultimos 7 dias (hoy incluido), de mas antiguo a mas reciente, marcando
 * cual tiene al menos un ejercicio completado.
 *
 * Se construye desde `GET /ejercicios/progreso` y NO desde `rachaDias`: la
 * racha se borra con un solo dia fallido, mientras que aqui un dia perdido
 * solo deja un hueco. Es la diferencia entre castigar y registrar.
 */
export function diasDeLaSemana(
  progreso: readonly ProgresoEjercicio[],
  hoy: Date = new Date(),
): DiaSemana[] {
  const dias: DiaSemana[] = [];

  for (let i = 6; i >= 0; i -= 1) {
    const fecha = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - i);
    const completado = progreso.some((p) => {
      const f = new Date(p.fechaCompletado);
      return (
        f.getFullYear() === fecha.getFullYear() &&
        f.getMonth() === fecha.getMonth() &&
        f.getDate() === fecha.getDate()
      );
    });

    dias.push({
      iso: aIsoLocal(fecha),
      dia: ETIQUETAS_DIA[fecha.getDay()],
      fecha,
      esHoy: i === 0,
      completado,
    });
  }

  return dias;
}

/* ------------------------------------------------------- historial (P7) -- */

/**
 * Historial de lo que el estudiante hizo, agrupado por dia (P7 del plan de
 * cierre).
 *
 * DE DONDE SALE Y POR QUE NO HACE FALTA BACKEND
 * --------------------------------------------
 * El plan daba P7 por bloqueado porque `GamificacionController` devuelve mocks.
 * Es verdad, pero **no hace falta tocarlo**: los dos datos que cruza esta
 * funcion ya tienen endpoint propio, y los dos son consultas Prisma reales,
 * no fixtures:
 *
 *   GET /ejercicios/progreso        -> `prisma.progresoEjercicio` (fechaCompletado)
 *   GET /perfil/historial-evaluaciones -> `prisma.resultado`     (fechaEvaluacion)
 *
 * Ambos tienen indice por fecha en `schema.prisma`. El cruce se hace aqui,
 * en el cliente, y por eso no se anade ni un endpoint ni una migracion.
 *
 * QUE NO ES ESTO
 * --------------
 * La lista de actividad que quito la Fase 5.8 mostraba datos **inventados**.
 * Esto no: sale de filas reales de la base de datos. El riesgo que queda no es de
 * datos, es de diseño, y por eso este archivo lleva las reglas de la Fase 4
 * escritas al lado del codigo que las cumple.
 */

/** Una actividad, ya reducida a lo que se muestra. */
export interface ActividadHistorial {
  /** Clave estable para el `key` de React. */
  id: string;
  /** `YYYY-MM-DD` en hora local: agrupa por dia. */
  dia: string;
  /** Texto ya escrito, en pasado. Nunca "1 actividad". */
  etiqueta: string;
  /** Que modulo se abrio al tocarla. */
  modulo: 'ejercicios' | 'evaluacion';
  /** Hora local `HH:MM`, para ordenar dentro del dia. */
  hora: string;
}

export type FuenteHistorial = {
  /** `GET /ejercicios/progreso` */
  ejercicios?: readonly ProgresoEjercicio[];
  /** `GET /perfil/historial-evaluaciones` */
  evaluaciones?: readonly { fechaEvaluacion: string }[];
};

const DIAS_VISIBLES = 30;

/** Maximos hechos que se muestran de un mismo dia. Ver la nota del agrupado. */
const MAX_POR_DIA = 2;

/**
 * Cruza las dos fuentes y devuelve como mucho una entrada por dia.
 *
 * UNA ENTRADA POR DIA, NO UN CONTADOR. La Fase 4 prohibio expresamente los
 * numeros que presionan ("12 actividades este mes"): un total invariado es un
 * recordatorio constante de lo que no se ha hecho. Por eso si un dia tiene tres
 * ejercicios, se muestra el primero y no "3".
 *
 * QUE SE CUENTA Y QUE NO
 * ----------------------
 * - **Ejercicios**: solo los `completado: true`. Un intento abandonado no es una
 *   actividad, y contarlo seria medir el fracaso.
 * - **Evaluaciones**: todas. Es un hito, no una repeticion.
 *
 * Si dos fuentes caen en el mismo dia, gana la de fecha mas reciente: se
 * muestra lo ultimo que hizo, que es lo que recuerda.
 */
export function historialPorDia(
  fuentes: FuenteHistorial,
  hoy: Date = new Date(),
): ActividadHistorial[] {
  const candidatos: ActividadHistorial[] = [];
  const desde = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() - (DIAS_VISIBLES - 1));

  const dentro = (fecha: Date) => fecha >= desde && fecha <= new Date(hoy.getTime() + 86_400_000);
  const hhmm = (f: Date) =>
    `${String(f.getHours()).padStart(2, '0')}:${String(f.getMinutes()).padStart(2, '0')}`;

  for (const e of fuentes.ejercicios ?? []) {
    // Un ejercicio no completado es un intento, no una actividad.
    if (!e.completado) continue;

    const f = new Date(e.fechaCompletado);
    if (Number.isNaN(f.getTime()) || !dentro(f)) continue;

    candidatos.push({
      id: `ejercicio-${e.id}`,
      dia: aIsoLocal(f),
      etiqueta: 'Completaste un ejercicio',
      modulo: 'ejercicios',
      hora: hhmm(f),
    });
  }

  for (const r of fuentes.evaluaciones ?? []) {
    const f = new Date(r.fechaEvaluacion);
    if (Number.isNaN(f.getTime()) || !dentro(f)) continue;

    candidatos.push({
      id: `evaluacion-${f.getTime()}`,
      dia: aIsoLocal(f),
      etiqueta: 'Completaste una evaluacion',
      modulo: 'evaluacion',
      hora: hhmm(f),
    });
  }

  // POR DIA SE PERMITEN HASTA DOS HECHOS DISTINTOS, NO UNO
  // ---------------------------------------------------
  // La primera version de esta funcion guardaba UNA entrada por dia y hacia
  // desaparecer el resto. Con un ejercicio y una evaluacion el mismo dia solo
  // se veia el mas reciente, y el otro hecho se perdia.
  //
  // Lo que se confunde ahi son dos cosas distintas:
  //
  //   - un CONTADOR:    "3 ejercicios" es un numero que mide rendimiento, y la
  //                     Fase 4 lo prohibe expresamente.
  //   - un HECHO:       "Completaste un ejercicio" y "Completaste una
  //                     evaluacion" son dos cosas que pasaron. No son un
  //                     numero, son un registro.
  //
  // Así que el límite es dos hechos por dia, NO uno. Y el recorte es a propósito:
  // un dia con doce ejercicios no debe ocupar doce lineas, porque entonces el
  // historial deja de ser un resumen y se vuelve la cifra de produccion del estudiante.
  //
  // Cuando hay mas de dos, se quedan las dos mas recientes y NO se dice "y N
  // mas": would've que volver a contar.
  const porDia = new Map<string, ActividadHistorial[]>();

  for (const a of candidatos) {
    const lista = porDia.get(a.dia);
    if (lista) lista.push(a);
    else porDia.set(a.dia, [a]);
  }

  const salida: ActividadHistorial[] = [];

  for (const lista of porDia.values()) {
    lista.sort((a, b) => b.hora.localeCompare(a.hora));
    salida.push(...lista.slice(0, MAX_POR_DIA));
  }

  // De mas reciente a mas antiguo.
  return salida.sort((a, b) =>
    a.dia === b.dia ? b.hora.localeCompare(a.hora) : b.dia.localeCompare(a.dia),
  );
}

/** Dia legible: "Hoy", "Ayer" o "hace 3 dias". Nunca la fecha cruda. */
export function etiquetaDia(iso: string, hoy: Date = new Date()): string {
  const [a, m, d] = iso.split('-').map(Number);
  if (!a || !m || !d) return iso;

  const fecha = new Date(a, m - 1, d);
  const dias = Math.round(
    (new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate()).getTime() - fecha.getTime()) /
      86_400_000,
  );

  if (dias === 0) return 'Hoy';
  if (dias === 1) return 'Ayer';
  if (dias < 7) return `Hace ${dias} dias`;
  if (dias < 14) return 'La semana pasada';

  return fecha.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

/* ------------------------------------------------------------------ slots -- */

export type SlotAcento = 'rosa' | 'azul' | 'violeta' | 'verde' | 'ambar' | 'lima' | 'rojo';

/**
 * Tonos de cada slot: `100` superficie, `500` acento, `700` texto.
 *
 * Referencia a `var(--v-*)` y no hex fijo. Motivo (P0 del plan de cierre): la
 * paleta oscura ya existia en `index.css:122-146`, con los pares 100/500/700 de
 * los seis slots redefinidos, pero `ACENTOS` usaba literales: en modo oscuro el
 * dashboard salia con contenedores pastel claros (`#FCE7F3`) sobre fondo
 * `#101a24`, con la tinta 700 oscura encima. `var()` resuelve en el elemento que
 * lo consume, asi que basta con referenciar el token para que `.dark` lo
 * sobreescriba sin cambiar ningun consumidor.
 *
 * REGLA: si se anade un `--v-*` nuevo, su override en `.dark` va en el MISMO
 * commit. Si no, se repite este mismo bug.
 */
export const ACENTOS: Record<SlotAcento, { bg: string; container: string; ink: string }> = {
  rosa: { container: 'var(--v-rosa-100)', bg: 'var(--v-rosa-500)', ink: 'var(--v-rosa-700)' },
  azul: { container: 'var(--v-azul-100)', bg: 'var(--v-azul-500)', ink: 'var(--v-azul-700)' },
  violeta: {
    container: 'var(--v-violeta-100)',
    bg: 'var(--v-violeta-500)',
    ink: 'var(--v-violeta-700)',
  },
  verde: { container: 'var(--v-verde-100)', bg: 'var(--v-verde-500)', ink: 'var(--v-verde-700)' },
  ambar: { container: 'var(--v-ambar-100)', bg: 'var(--v-ambar-500)', ink: 'var(--v-ambar-700)' },
  lima: { container: 'var(--v-lima-100)', bg: 'var(--v-lima-500)', ink: 'var(--v-lima-700)' },
  rojo: { container: 'var(--v-rojo-100)', bg: 'var(--v-rojo-500)', ink: 'var(--v-rojo-700)' },
};
